import { subscribeToLocation } from "@swan-io/chicane";
import { ResultOf } from "gql.tada";
import React, { Fragment, useEffect, useState } from "react";
import { Pressable, PressableProps, ScrollView, StyleSheet, View, ViewProps } from "react-native";
import { P, match } from "ts-pattern";
import { Except } from "type-fest";
import { OrganizationListQuery } from "../pages/OrgListPage";
import { animDuration, colors, invariantColor, radii } from "../utils/design";
import { useQuery } from "../utils/graphql";
import { t } from "../utils/i18n";
import { MeQuery } from "../utils/queries";
import { router } from "../utils/routes";
import { IconAvatar, OrgAvatar, PlaceholderAvatar, UserAvatar } from "./Avatar";
import { Icon } from "./Icon";
import { AppsSection, GeneralSection } from "./Sidebar";
import { Fill, Space } from "./Space";
import { PressableLink, Text } from "./Text";
import { TransitionView } from "./TransitionView";

export const OPEN_MENU_HEIGHT = 115;

const styles = StyleSheet.create({
  fill: {
    flex: 1,
  },

  enterAnimation: {
    animationKeyframes: {
      from: {
        transform: "translateX(100%)",
      },
      to: {
        transform: "translateX(0)",
      },
    },
    animationDuration: animDuration[300],
  },
  exitAnimation: {
    animationKeyframes: {
      from: {
        transform: "translateX(0)",
      },
      to: {
        transform: "translateX(100%)",
      },
    },
    animationDuration: animDuration[300],
    animationFillMode: "forwards",
  },

  openBackground: {
    zIndex: 1,
    position: "absolute",
    left: 0,
    bottom: 0,
    width: "100%",
    height: OPEN_MENU_HEIGHT,
    backgroundImage: "linear-gradient(360deg, #000000 0.01%, #151515 50.5%, rgba(0, 0, 0, 0) 100%)",
    pointerEvents: "none",
  },

  openButton: {
    zIndex: 2,
    position: "absolute",
    bottom: 36,
    left: 24,
    right: 24,
  },

  button: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
    paddingVertical: 8,
    backgroundColor: colors.neutral[900],
    borderRadius: radii[4],
    borderWidth: 1,
    borderColor: colors.neutral[700],
  },
  buttonLarge: {
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  buttonHover: {
    backgroundColor: colors.neutral[800],
  },
  buttonPressed: {
    backgroundColor: colors.neutral[700],
  },

  menu: {
    zIndex: 3,
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: colors.neutral[800],
    paddingBottom: 36,
  },
  menuPage: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    paddingTop: 24,
    paddingHorizontal: 12,
    backgroundColor: colors.neutral[800],
  },
  menuContent: {
    paddingHorizontal: 12,
  },

  transparentButton: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: radii[4],
    transitionDuration: animDuration[200],
    transitionProperty: "background-color",
    outlineWidth: 0,
  },
  transparentButtonHover: {
    backgroundColor: colors.neutral[600],
  },
  transparentButtonNegativeHover: {
    backgroundColor: colors.neutral[600],
  },
  transparentButtonLarge: {
    paddingVertical: 12,
  },

  backButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 4,
  },

  plusIcon: {
    backgroundColor: colors.neutral[700],
  },
});

type MenuState = "closed" | "links" | "user" | "organizations";

export const MobileMenu: React.FC = () => {
  const [state, setState] = useState<MenuState>("closed");
  const route = router.useRoute(["OrgArea", "OrgList"]);

  const {
    data: { organizations },
  } = useQuery(OrganizationListQuery);
  const orgSlug = route?.name === "OrgArea" ? route.params.org_slug : undefined;
  const currentOrg = organizations.find((org) => org.slug === orgSlug);

  const closeMenu = () => setState("closed");
  const backToLinks = () => setState("links");

  useEffect(() => {
    const unsubscribe = subscribeToLocation(closeMenu);
    return unsubscribe;
  }, []);

  return (
    <>
      <View style={styles.openBackground} />
      <MenuButton style={styles.openButton} onPress={() => setState("links")}>
        <Text variant="small" color={invariantColor.white}>
          {t("mobile_menu.open")}
        </Text>
      </MenuButton>

      <TransitionView
        visible={state !== "closed"}
        style={styles.menu}
        enterAnimation={styles.enterAnimation}
        exitAnimation={styles.exitAnimation}
      >
        <ScrollView contentContainerStyle={styles.fill}>
          <View style={styles.menuPage}>
            <Fill />

            {match(route)
              .with({ name: "OrgArea" }, ({ params: { org_slug } }) => (
                <View>
                  <GeneralSection orgSlug={org_slug} />
                  <Space height={24} />
                  <AppsSection orgSlug={org_slug} />
                  <Space height={24} />

                  <UserButton onPress={() => setState("user")} />
                  <Space height={12} />
                  <OrgsButton currentOrg={currentOrg} onPress={() => setState("organizations")} />
                </View>
              ))
              .with({ name: "OrgList" }, () => <UserButton onPress={() => setState("user")} />)
              .with(P.nullish, () => (
                <View>
                  <UserButton onPress={() => setState("user")} />
                  <Space height={12} />
                  <OrgsButton currentOrg={undefined} onPress={() => setState("organizations")} />
                </View>
              ))
              .exhaustive()}
          </View>

          <TransitionView
            visible={state === "user"}
            style={styles.menuPage}
            enterAnimation={styles.enterAnimation}
            exitAnimation={styles.exitAnimation}
          >
            <UserMenu onBack={backToLinks} />
          </TransitionView>
          <TransitionView
            visible={state === "organizations"}
            style={styles.menuPage}
            enterAnimation={styles.enterAnimation}
            exitAnimation={styles.exitAnimation}
          >
            <OrgsMenu organizations={organizations} currentOrg={currentOrg} onBack={backToLinks} />
          </TransitionView>
        </ScrollView>

        <Space height={12} />

        <View style={styles.menuContent}>
          <MenuButton onPress={closeMenu}>
            <Text variant="small" color={invariantColor.white}>
              {t("mobile_menu.close")}
            </Text>
          </MenuButton>
        </View>
      </TransitionView>
    </>
  );
};

type MenuButtonProps = Except<PressableProps, "style"> & {
  large?: boolean;
  style?: ViewProps["style"];
};

const MenuButton: React.FC<MenuButtonProps> = ({ large = false, style, ...props }) => {
  return (
    <Pressable
      style={({ pressed, focused, hovered }) => [
        styles.button,
        large && styles.buttonLarge,
        (hovered || focused) && styles.buttonHover,
        pressed && styles.buttonPressed,
        style,
      ]}
      {...props}
    />
  );
};

type UserButtonProps = {
  onPress: () => void;
};

const UserButton: React.FC<UserButtonProps> = ({ onPress }) => {
  const { data } = useQuery(MeQuery);
  const { fullName, avatarUrl, updatedAt } = data.me;

  return (
    <MenuButton large={true} onPress={onPress}>
      <UserAvatar size="regular" name={fullName} url={avatarUrl} updatedAt={updatedAt} />
      <Space width={12} />
      <Text variant="small" color={invariantColor.white} ellipsis={true} style={styles.fill}>
        {fullName}
      </Text>
      <Space width={12} />
      <Icon name="chevron_right" color={colors.neutral[600]} />
    </MenuButton>
  );
};

type UserMenuProps = {
  onBack: () => void;
};

const UserMenu: React.FC<UserMenuProps> = ({ onBack }) => {
  return (
    <>
      <Fill />

      <PressableLink
        to={router.MyProfile()}
        variant="body"
        color={invariantColor.white}
        style={({ pressed, hovered, focused }) => [
          styles.transparentButton,
          (pressed || hovered || focused) && styles.transparentButtonHover,
        ]}
      >
        {t("sidebar.my_profile")}
      </PressableLink>
      <PressableLink
        to={router.MyProfile()}
        variant="body"
        color={colors.negative[500]}
        style={({ pressed, hovered, focused }) => [
          styles.transparentButton,
          (pressed || hovered || focused) && styles.transparentButtonHover,
        ]}
      >
        {t("sidebar.sign_out")}
      </PressableLink>

      <Space height={28} />

      <BackButton onPress={onBack} />
    </>
  );
};

type OrgsButtonProps = {
  currentOrg: ResultOf<typeof OrganizationListQuery>["organizations"][number] | undefined;
  onPress: () => void;
};

const OrgsButton: React.FC<OrgsButtonProps> = ({ currentOrg, onPress }) => {
  return (
    <MenuButton large={true} onPress={onPress}>
      {currentOrg != null ? (
        <OrgAvatar
          size="regular"
          name={currentOrg.name}
          url={currentOrg.logoUrl}
          updatedAt={currentOrg.updatedAt}
        />
      ) : (
        <PlaceholderAvatar />
      )}

      <Space width={12} />
      <Text variant="small" color={invariantColor.white} ellipsis={true} style={styles.fill}>
        {currentOrg?.name ?? t("component.org_select.placeholder")}
      </Text>

      <Space width={12} />
      <Icon name="chevron_right" color={colors.neutral[600]} />
    </MenuButton>
  );
};

type OrgsMenuProps = {
  organizations: ResultOf<typeof OrganizationListQuery>["organizations"];
  currentOrg: ResultOf<typeof OrganizationListQuery>["organizations"][number] | undefined;
  onBack: () => void;
};

const OrgsMenu: React.FC<OrgsMenuProps> = ({ currentOrg, organizations, onBack }) => {
  return (
    <>
      <Fill />

      {organizations
        .filter((org) => org.id !== currentOrg?.id)
        .map((org) => (
          <Fragment key={org.id}>
            <PressableLink
              key={org.id}
              to={router.OrgHome({ org_slug: org.slug })}
              style={({ pressed, hovered, focused }) => [
                styles.transparentButton,
                styles.transparentButtonLarge,
                (pressed || hovered || focused) && styles.transparentButtonHover,
              ]}
            >
              <OrgAvatar
                size="regular"
                name={org.name}
                url={org.logoUrl}
                updatedAt={org.updatedAt}
              />

              <Space width={12} />

              <Text variant="body" color={invariantColor.white}>
                {org.name}
              </Text>
            </PressableLink>
          </Fragment>
        ))}

      <PressableLink
        to={router.OrgNewIndex()}
        style={({ pressed, hovered, focused }) => [
          styles.transparentButton,
          styles.transparentButtonLarge,
          (pressed || hovered || focused) && styles.transparentButtonHover,
        ]}
      >
        <IconAvatar
          size="regular"
          name="plus"
          color={colors.primary[500]}
          style={styles.plusIcon}
        />

        <Space width={12} />

        <Text variant="body" color={invariantColor.white}>
          {t("sidebar.create_organization")}
        </Text>
      </PressableLink>

      <Space height={16} />

      <BackButton onPress={onBack} />
    </>
  );
};

const BackButton: React.FC<Except<PressableProps, "style">> = (props) => {
  return (
    <Pressable style={styles.backButton} {...props}>
      <Icon name="arrow_left" size={16} color={colors.neutral[200]} />
      <Space width={12} />
      <Text variant="body" color={invariantColor.white}>
        {t("mobile_menu.back")}
      </Text>
    </Pressable>
  );
};
